/**
 * Shared helper for making an entity create/edit dialog **URL-addressable**
 * (campaign #15b, total URL addressability).
 *
 * Canonical pattern (#9, already in accounts/reports/transactions):
 *   - `useSearch({ strict: false })` reads the loose URL shape.
 *   - a local signal reflects the state; a `createEffect` synchronizes it
 *     **URL → signal** (back/forward, pasted links, reload).
 *   - `navigate({ replace: true })` writes **signal → URL** without flooding
 *     history.
 *   - defaults are **omitted** from the URL (idempotent, clean URLs).
 *
 * Parameter convention (shared by all routes):
 *   - `?new=1`        → create dialog open.
 *   - `?edit=<id>`    → edit dialog for entity `<id>`.
 *
 * Returns reactive accessors + actions that write to the URL. The component maps
 * `editId()` to the specific row from its query (and, if the row does not exist
 * or is not accessible, simply does not open the editor — PowerSync sync rules
 * guarantee that a resource without permission "does not exist" on the client).
 */

import { useNavigate, useSearch } from "@tanstack/solid-router";
import { type Accessor, createMemo } from "solid-js";

export interface DialogSearch {
  /** `true` if the URL requests the create dialog (`?new=1`). */
  isCreate: Accessor<boolean>;
  /** Entity id to edit (`?edit=<id>`), or `undefined`. */
  editId: Accessor<string | undefined>;
  /** `true` if either dialog should be open. */
  isOpen: Accessor<boolean>;
  /** Opens the create dialog (writes `?new=1`). */
  openCreate: () => void;
  /** Opens the edit dialog for `<id>` (writes `?edit=<id>`). */
  openEdit: (id: string) => void;
  /** Closes both dialogs (clears `new`/`edit` from the URL). */
  close: () => void;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Active `?new`. TanStack's global registry unions `new` types from all routes
 * (in debts it is `owed_to_me|i_owe`), so we compare via `String` to avoid
 * clashing with the union type and accept "1"/1/true from the others.
 */
function isCreateFlag(value: unknown): boolean {
  return value === true || String(value) === "1";
}

/**
 * Links a route's `new`/`edit` params to the state of a create/edit dialog.
 * `to` is the route path (e.g. `"/budgets"`).
 */
export function useDialogSearch(to: string): DialogSearch {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });

  const isCreate = createMemo(() => isCreateFlag(rawSearch().new));
  const editId = createMemo(() => asString(rawSearch().edit));
  const isOpen = createMemo(() => isCreate() || editId() !== undefined);

  const patch = (next: { new?: 1; edit?: string }) => {
    void navigate({
      to,
      replace: true,
      // Preserve the rest of the route params; only touch new/edit.
      search: (prev: Record<string, unknown>) => {
        const { new: _new, edit: _edit, ...rest } = prev;
        return { ...rest, ...next };
      },
    });
  };

  return {
    isCreate,
    editId,
    isOpen,
    openCreate: () => {
      patch({ new: 1 });
    },
    openEdit: (id: string) => {
      patch({ edit: id });
    },
    close: () => {
      patch({});
    },
  };
}

/**
 * Reusable `validateSearch`: normalizes `new`/`edit` and discards everything
 * else. Routes with only these two params can use it as-is; routes adding more
 * (e.g. `kind`, `parent`, `month`) compose it.
 */
export function sanitizeDialogSearch(search: Record<string, unknown>): {
  new?: 1;
  edit?: string;
} {
  const isNew = search.new === true || search.new === "1" || search.new === 1;
  const edit = asString(search.edit);
  return {
    new: isNew && edit === undefined ? 1 : undefined,
    edit,
  };
}
